import React, { useContext } from 'react';
import { MergedPanel } from './StatRuneSwitch';
import { setGlyphSrc } from './RuneStar';
import { Unit, Rune, Artifact, statsWith, setCounts, ATTRIBUTE_NAME, SET_NAME } from './runeEngine';

/**
 * **몹 창 — 게임에서 몹을 눌렀을 때 뜨는 스탯 판.**
 *
 *   [초상]  Lv.40 이카루
 *          (세트 문양)
 *   체력        9885  +2175
 *   공격력       637   +307
 *   방어력       736  +1754
 *   공격속도     108    +56
 *   치명 확률     22%
 *   치명 피해     74%
 *   효과 저항     28%
 *   효과 적중     44%
 *
 * 왼쪽 숫자는 몹의 기본값, 초록은 끼운 룬 여섯 개(지금 화면의 빌드)로 오른 만큼. 치확·치피·
 * 효저·효적은 게임처럼 합친 값 하나로 적는다. 아티팩트는 안 넣는다.
 *
 * 파란 "(+N)" 은 이 룬 여섯 개를 끝까지 키우면(풀강화·변환·풀연마 — `peakRunes`) 더 오르는 만큼.
 * 이미 고점이면 0 이라 안 적는다.
 *
 * **초상을 누르면 몹을 바꾼다**(사이트의 몹 고르기 창). 고른 몹이 계정에 없으면(`unit` 이 null)
 * 초상과 이름은 그리고 스탯 자리에 그렇게 적는다 — 기본값이 계정 JSON 에서만 온다.
 */

/**
 * 속성 아이콘 — 서버가 내주는 게임 그림(`/images/water.png` 등)을 그대로 쓴다.
 * 1 물 · 2 불 · 3 바람 · 4 빛 · 5 어둠.
 */
const ATTR_FILE: Record<number, string> = { 1: 'water', 2: 'fire', 3: 'wind', 4: 'light', 5: 'dark' };
function AttrIcon({ attribute, size }: { attribute: number; size: number }) {
    const file = ATTR_FILE[attribute];
    if (!file) return null;
    return (
        <img loading="lazy"
            src={`/images/${file}.png`} alt={ATTRIBUTE_NAME[attribute] || ''} title={ATTRIBUTE_NAME[attribute] || ''}
            style={{ width: size, height: size, flexShrink: 0, objectFit: 'contain', filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.5))' }}
        />
    );
}

const OUTLINE = '-1px 0 #1a0f08, 1px 0 #1a0f08, 0 -1px #1a0f08, 0 1px #1a0f08';
const text = (size: number, color: string, weight = 500): React.CSSProperties => ({ fontSize: size, color, fontWeight: weight, textShadow: OUTLINE });

/** 100 이 끝인 스탯 — 다 차면 숫자를 빨갛게 */
const CAPPED = new Set(['cr', 'res', 'acc']);

/** 끼운 룬으로 다 찬 세트 (같은 세트 두 벌이면 둘, 4세트 먼저) — 무형은 setCounts 가 모자란 조각에 넣는다 */
const FOUR = new Set([3, 5, 8, 10, 11, 13]);
function completedSets(runes: Rune[]): number[] {
    const count = setCounts(runes);
    const out: number[] = [];
    for (const [id, n] of Object.entries(count)) for (let k = 0; k < Math.floor(n / (FOUR.has(Number(id)) ? 4 : 2)); k++) out.push(Number(id));
    return out.sort((a, b) => Number(FOUR.has(b)) - Number(FOUR.has(a)));
}

export default function MonsterStatPanel({ unit, runes, peakRunes, artifacts, sets, name, img, isMobile, onPortraitClick, editCell, headerExtra }: {
    unit: Unit | null;
    runes: Rune[];
    /** 같은 룬을 끝까지 키운 것 — 파란 (+N) 을 낼 때 */
    peakRunes?: Rune[];
    /** 실제로 낀 아티팩트 — 주면 그 주옵으로 센다(전투기록). 안 주면 옵티마이저처럼 공격력 +200 으로 본다 */
    artifacts?: Artifact[];
    /** 룬 세트 — 개별 룬 없이 세트만 아는 경우(공략에 수치만 적은 몹) 이름 아래에 붙인다 */
    sets?: number[];
    name: string;
    img: string | null;
    isMobile: boolean;
    onPortraitClick?: () => void;
    /**
     * **입력 모드** — 주면 값 칸 대신 이걸 그린다(공략에 룬 세팅을 적을 때). 체력·공격력·방어력·공속은
     * 초록 칸(더한 만큼) 자리에, 퍼센트 스탯은 합계 자리에 들어간다 — 게임 몹 창에 보이는 그대로 옮겨 적게.
     */
    editCell?: (key: string, pct: boolean) => React.ReactNode;
    /** 머리(초상·이름) 아래에 끼울 것 — 입력 모드의 세트 고르기 */
    headerExtra?: React.ReactNode;
}) {
    const rows = unit ? statsWith(unit, runes, artifacts) : [];
    const peakRows = unit && peakRunes ? statsWith(unit, peakRunes, artifacts) : null;
    const more = (i: number) => (peakRows ? peakRows[i].bonus - rows[i].bonus : 0);
    // 세트 문양 — 세트만 아는 몹은 받은 것, 룬을 낀 몹은 다 찬 세트(4세트 먼저) (사용자 2026-10-28 "여기도 똑같이 표시해")
    const shownSets = sets && sets.length ? sets : completedSets(runes);

    // 폰은 작게 — [스탯|룬] 전환 칸에 룬 창과 같은 크기로 들어간다(사용자 지시: 창 여유를 확보)
    const f = isMobile ? 0.62 : 1;
    // 룬 창과 한 상자에 들어가면 바탕·테두리는 상자가 그린다
    const merged = useContext(MergedPanel);

    return (
        <div style={{
            color: '#f4efe6', fontFamily: 'inherit',
            ...(merged ? {} : {
                borderRadius: 16, padding: `${12 * f}px ${16 * f}px ${14 * f}px`,
                background: 'linear-gradient(180deg, #2f1f16 0%, #23160f 100%)', border: '1px solid #5a3d24',
                boxShadow: '0 8px 20px rgba(0,0,0,0.35)'
            }),
            // 바깥 카드가 가운데 정렬이면 여기까지 흘러들어 짧은 이름("체력")이 제 칸에서 가운데로 밀린다
            textAlign: 'left'
        }}>
            {/* 머리 — 초상 · 이름 · 성급 */}
            <div style={{ display: 'flex', gap: 14 * f, alignItems: 'center' }}>
                {/*
                  초상 — 누르면 몹을 바꾼다. 바꿀 수 있다는 티를 낸다: 금테가 은은히 뛰고(`mob-swap-pulse`),
                  마우스를 올리면 그림이 살짝 어두워지며 오른아래에 연필이 뜬다.
                */}
                <div
                    role={onPortraitClick ? 'button' : undefined} onClick={onPortraitClick}
                    title={onPortraitClick ? '눌러서 몹 바꾸기' : undefined}
                    className={onPortraitClick ? 'mob-swap' : undefined}
                    style={{
                        position: 'relative', width: 84 * f, height: 84 * f, flexShrink: 0, borderRadius: 10, overflow: 'hidden',
                        border: `${3 * f}px solid #c9a15a`, background: '#3a2216',
                        cursor: onPortraitClick ? 'pointer' : 'default'
                    }}
                >
                    {img && <img loading="lazy" src={img} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                    {onPortraitClick && (
                        <span className="mob-swap-mark" style={{
                            position: 'absolute', right: 3 * f, bottom: 3 * f, width: 22 * f, height: 22 * f, borderRadius: '50%',
                            background: 'rgba(26, 15, 8, 0.8)', border: `1.5px solid #f2b23a`, color: '#f2b23a',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 * f, lineHeight: 1
                        }}>✎</span>
                    )}
                </div>
                <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 * f, whiteSpace: 'nowrap' }}>
                        {unit && <AttrIcon attribute={unit.attribute} size={22 * f} />}
                        {unit && <span style={text(18 * f, '#f2b23a', 600)}>Lv.{unit.level}</span>}
                        <span style={text(22 * f, '#fff', 600)}>{name}</span>
                    </div>
                    {/* 성급(★)은 안 그린다 — 그 자리에 세트 문양을 크게 (사용자 2026-10-28 "6성인거 표시하지말고 대신 문양을 더 키워봐") */}
                    {!unit && (
                        <div style={{ marginTop: 4 * f, fontSize: 12 * f, color: '#e08a6a', fontWeight: 700 }}>계정에 이 몹이 없어 스탯을 낼 수 없습니다</div>
                    )}
                    {shownSets.length > 0 && (
                        <div style={{ marginTop: 4 * f, display: 'flex', gap: 0, flexWrap: 'wrap' }}>
                            {/* 세트 문양 (사용자 2026-10-28 "폭주 의지 이렇게보다 문양을 넣어보자") — 이름은 마우스를 올리면 */}
                            {shownSets.map((id, i) => {
                                const glyph = setGlyphSrc(id);
                                const nm = SET_NAME[id] || `세트${id}`;
                                return glyph ? (
                                    <img key={i} src={glyph} alt={nm} title={nm} style={{
                                        // 문양 그림 가장자리 빈 곳만큼 겹친다 (사용자 2026-10-28 "문양 사이를 좀 더 좁혀봐")
                                        width: 40 * f, height: 40 * f, marginLeft: i ? -6 * f : 0, objectFit: 'contain', filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.7))'
                                    }} />
                                ) : (
                                    <span key={i} style={{
                                        padding: `${2 * f}px ${9 * f}px`, borderRadius: 999, background: '#4c2d15', border: '1px solid #7a4a22',
                                        ...text(13 * f, '#f2b23a', 600)
                                    }}>{nm}</span>
                                );
                            })}
                        </div>
                    )}
                    {onPortraitClick && <div style={{ marginTop: 3 * f, fontSize: 11 * f, color: '#b8a58c', fontWeight: 700 }}>몹을 누르면 몹을 변경할 수 있습니다</div>}
                </div>
            </div>

            {headerExtra}

            {/* 스탯 — 기본 / 룬으로 오른 만큼(초록) */}
            {/* 이름 칸 · 숫자 칸 · 초록 칸 — 내용 너비만큼만 차지해 이름과 숫자가 붙어 보인다(게임처럼) */}
            {/*
              게임 몹 창처럼 — 이름은 왼쪽, **숫자는 한 자리에 오른쪽 맞춤**(퍼센트 값도 같은 자리),
              오른 만큼(초록)과 (더 오를 만큼)은 그 오른쪽에 붙는다.
            */}
            {unit && <div style={{ marginTop: 12 * f, display: 'grid', gridTemplateColumns: 'max-content max-content max-content', justifyContent: 'start', columnGap: 12 * f, rowGap: 3 * f, alignItems: 'baseline' }}>
                {rows.map((r, i) => {
                    const gapRow = r.key === 'cr';   // 게임처럼 네 줄 뒤에 한 칸 띈다
                    const cell = (v: React.ReactNode, style: React.CSSProperties, extra: React.CSSProperties = {}) => (
                        <div style={{ ...style, ...extra, marginTop: gapRow ? 10 * f : 0 }}>{v}</div>
                    );
                    const up = more(i);
                    // 변환으로 깎이는 스탯도 있다(체력 부옵을 공격력으로 갈아엎으면 체력은 준다) — 음수는 주황으로
                    const upText = up ? `(${up > 0 ? '+' : '−'}${Math.abs(up).toLocaleString()}${r.pct ? '%' : ''})` : '';
                    const upSpan = upText
                        ? <span style={{ ...text(17 * f, up > 0 ? '#5cc8ff' : '#e0a06a'), marginLeft: r.pct ? 0 : 6 * f }}>{upText}</span>
                        : null;
                    return (
                        <React.Fragment key={r.key}>
                            {cell(r.label, text(20 * f, '#f4efe6'), { textAlign: 'left' })}
                            {cell(
                                editCell && r.pct ? editCell(r.key, true) : (r.pct ? `${r.base + r.bonus}%` : r.base.toLocaleString()),
                                // 치확·효저·효적은 100 이 끝 — 다 찼으면 빨갛게(사용자 지시)
                                text(20 * f, CAPPED.has(r.key) && r.base + r.bonus >= 100 ? '#ff5a5a' : '#fff'), { textAlign: 'right' }
                            )}
                            {cell(
                                editCell && !r.pct
                                    ? editCell(r.key, false)
                                    : <>{!r.pct && r.bonus ? `${r.bonus > 0 ? '+' : '−'}${Math.abs(r.bonus).toLocaleString()}` : ''}{upSpan}</>,
                                text(20 * f, '#7ee36a'), { textAlign: 'left', whiteSpace: 'nowrap' }
                            )}
                        </React.Fragment>
                    );
                })}
            </div>}
        </div>
    );
}
